import { Component, type ErrorInfo, type ReactNode } from 'react'

type Props = { children: ReactNode }
type State = { error: Error | null }

function isLikelyChunkError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error)
  const name = error instanceof Error ? error.name : ''
  return (
    name === 'ChunkLoadError' ||
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i.test(
      msg,
    )
  )
}

/**
 * Failed lazy chunks and render errors otherwise unmount the whole tree
 * (blank white screen). Reload usually recovers after a stale deploy.
 */
export class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Route render failed', error, info.componentStack)
  }

  render() {
    if (!this.state.error) return this.props.children

    const chunkError = isLikelyChunkError(this.state.error)
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-[#f7fbfb] px-6 text-center">
        <p className="text-lg font-semibold text-slate-900">This page did not load</p>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          {chunkError
            ? 'A script for this screen failed to load. Reloading usually fixes it.'
            : 'Something went wrong while opening this screen.'}
        </p>
        <button
          type="button"
          className="mt-6 rounded-xl bg-teal-800 px-5 py-2.5 text-sm font-semibold text-white"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    )
  }
}
